<template>
  <Teleport to="#teleports">
    <Transition>
      <div
        v-if="modelValue"
        class="dialog-mask bg-blur"
        @click.self="closeDialog"
      >
        <div class="dialog">
          <div class="flex items-center justify-between mb-8">
            <span class="local-title">
              {{ title }}
            </span>
            <AppBtn
              width="24"
              height="24"
              class="close-button"
              @click="closeDialog"
            >
              <AppIcon name="close_modal_window_icon" />
            </AppBtn>
          </div>
          <div class="dialog-content">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'AppDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    isBodyOverflowScroll: {
      type: Boolean,
      default: false
    },
    openQueryParam: {
      type: String,
      default: '',
    },
    openQueryValue: {
      type: [String, Array],
      default: null,
    }
  },
  watch: {
    modelValue(isVisible) {
      if (this.isBodyOverflowScroll) return

      const body = document.body
      if (!body) return;

      if (isVisible) {
        body.classList.add('body-modal-open');
      }
      else {
        body.classList.remove('body-modal-open');
      }
    },
    '$route.query': 'maybeOpenFromQuery',
  },
  mounted() {
    document.body.addEventListener('keydown', this.onEscapeHandler);
    this.maybeOpenFromQuery()
  },
  unmounted() {
    document.body.removeEventListener('keydown', this.onEscapeHandler);
  },
  methods: {
    maybeOpenFromQuery() {
      if (!this.openQueryParam || !this.$route || this.modelValue) return

      const query = this.$route.query || {}
      if (!Object.prototype.hasOwnProperty.call(query, this.openQueryParam)) return

      const normalizedValue = this.normalizeQueryValue(query[this.openQueryParam])
      const allowedValues = this.getAllowedQueryValues()
      const shouldOpen = allowedValues
        ? allowedValues.includes(normalizedValue)
        : normalizedValue !== '0' && normalizedValue !== 'false'

      if (shouldOpen) {
        this.$emit('update:modelValue', true)
      }
    },
    getAllowedQueryValues() {
      if (this.openQueryValue == null || this.openQueryValue === '') return null

      const values = Array.isArray(this.openQueryValue)
        ? this.openQueryValue
        : [this.openQueryValue]

      return values
        .map((value) => this.normalizeQueryValue(value))
        .filter((value) => value.length > 0)
    },
    normalizeQueryValue(value) {
      if (value === null || value === undefined) return ''
      if (Array.isArray(value)) return this.normalizeQueryValue(value[0])
      return String(value).trim().toLowerCase()
    },
    onEscapeHandler(event) {
      if (this.modelValue && event.keyCode === 27) {
        this.closeDialog()
      }
    },
    closeDialog() {
      this.$emit('update:modelValue', !this.modelValue)
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-mask {
  top: 0px;
  left: 0px;
  z-index: 50;
  width: 100vw;
  height: 100dvh;
  position: fixed;

  .dialog {
    top: 50%;
    left: 50%;
    width: 100%;
    padding: 32px;
    max-width: 532px;
    position: absolute;
    border-radius: 16px;
    background-color: $black;
    transform: translate(-50%, -50%);

    .local-title {
      color: $white;
    }

    .dialog-content {
      height: calc(100dvh - 180px);
      overflow-x: hidden;
      overflow-y: auto;
      margin-right: -12px;
      padding-right: 12px;
    }
  }
}

.close-button {
  padding: 0 !important;
}

@media only screen and (max-width: $sm) {
  .dialog-mask {
    .dialog {
      padding: 32px 16px;
      max-width: 100vw;
      height: 100dvh;

      .dialog-content {
        height: calc(100dvh - 130px);
      }
    }
  }
}
</style>
